<script setup>
import { defineProps, toRefs, computed, inject } from 'vue';
import { useIns } from 'src/utils/v2_helper/common';
import { PRODUCT_STATUS } from '../util';
import { useProductPrice } from '../../../utils/hooks/product/use-product-price';

const app = useIns();
const props = defineProps({
  free: {
    type: Boolean,
    default: false,
  },
  prod: {
    type: Object,
    default() {
      return {};
    },
  },
});
const { prod } = toRefs(props);
const { price, delPrice } = useProductPrice(prod);
const PRODUCT_STATUS_MAP = {
  [PRODUCT_STATUS.ITEM_GROUP_ACCEPTING]: {
    icon: 'accepting',
    text: app.$t('fullDinein.new'),
  },
  [PRODUCT_STATUS.ITEM_GROUP_PREPARING]: {
    icon: 'preparing',
    text: app.$t('fullDinein.preparing'),
  },
  [PRODUCT_STATUS.ITEM_GROUP_SERVED]: {
    icon: 'served',
    text: app.$t('fullDinein.served'),
  },
  [PRODUCT_STATUS.ITEM_GROUP_CANCELED]: {
    icon: 'canceled',
    text: app.$t('fullDinein.canceled'),
  },
};
const status = computed(
  () => PRODUCT_STATUS_MAP[prod.value.groupStatusId] || {},
);
const modifiers = computed(() => prod.value.modifiers || []);
const isCanceled = computed(
  () => prod.value.groupStatusId === PRODUCT_STATUS.ITEM_GROUP_CANCELED,
);

const showStatus = inject('show_product_status', false);
</script>
<template>
  <div>
    <div class="prod" :disabled="isCanceled">
      <div bold class="prod-count">x{{ prod.quantity }}</div>
      <div class="prod-info">
        <div class="prod-info-name" bold break>{{ prod.productName }}</div>
        <div class="prod-info-modif" break>
          <template v-for="(mo, i) in prod.modifiers">
            {{ mo.name }}
            {{ i !== prod.modifiers.length - 1 ? '/' : '' }}
          </template>
        </div>
        <!-- AI-assisted 2026-09 START: Show each ordered Add On beneath its parent product. -->
        <div
          v-for="addon in prod.addons || []"
          :key="addon.externalProductId"
          class="prod-info-modif"
          break
        >
          {{ addon.name }} x{{ addon.quantity }} {{ $pid(addon.totalPrice) }}
        </div>
        <!-- AI-assisted 2026-09 END -->
        <div class="prod-info-comment" break>{{ prod.comment || '' }}</div>
      </div>
      <div class="prod-tail">
        <!--pas: price and status-->
        <div class="prod-tail-pas">
          <div class="pas-price">
            {{
              free ? $t('checkout.free') : $pid(isCanceled ? delPrice : price)
            }}
          </div>
          <div v-if="price !== delPrice && !isCanceled" class="pas-price-del">
            {{ $pid(delPrice) }}
          </div>
          <div v-if="showStatus || isCanceled" class="pas-status">
            <span :class="`iconfont dm-${status.icon}`" />
            <b>{{ status.text }}</b>
          </div>
        </div>
      </div>
    </div>
    <slot name="pkg" />
  </div>
</template>

<style lang="scss" scoped>
.prod {
  padding-bottom: 12px;
  color: $grayFont;
  display: flex;
  align-items: flex-start;
  &-count {
    width: 20px;
    height: 20px;
    line-height: 20px;
    text-align: center;
    background: $grayBg;
    border-radius: $border-radius-1;
    outline: solid 2px $border-color-3;
    color: $grayFont;
    align-self: flex-start;
  }
  &-tail {
    margin-left: auto;
    &-pas {
      white-space: nowrap;
      margin-left: auto;
      text-align: right;
      .pas-status {
        color: #000;
      }
      .icon {
        fill: transparent;
        stroke: #ff9e2d;
      }
      b {
        margin-left: 2px;
      }
      .pas-price {
        text-align: right;
        color: #000;
        &-del {
          text-decoration: line-through;
        }
        b {
          color: $grayFont;
          font-size: 12px;
        }
      }
    }
  }
  &-info {
    display: flex;
    flex-direction: column;
    color: $black;
    margin-left: 6px;
    & > div + div {
      margin-top: 4px;
    }
    &-modif,
    &-comment {
      color: $grayFont;
      font-size: 12px;
    }
  }

  .dm-preparing {
    color: #0ea5e9;
  }

  .dm-canceled {
    color: #6b7280;
  }

  .dm-served {
    color: #10b981;
  }

  .dm-accepting {
    color: #ff9e2d;
  }
}
</style>
